import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	UserLicense,
	UserLicenseStatistics,
	getStatistics
} from "client/Api/FeaturePackages/featurePackageManagement";
import { UserLicenseList } from "./UserLicenseList";
import { UserLicenseCard, UserLicenseCardStatus } from "./UserLicenseCard";
import {
	FormattedText,
	Icon
} from "client/Components";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./UserLicenseManagementPage.module.less";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { Action, ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { useDefaultUserLicenseDialog } from "./DefaultUserLicenseDialog";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	route
} from "client/Navigation";
import { EditUserLicensePageParams } from "./EditUserLicensePage";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { UserLicenseDescription } from "./UserLicenseDescription";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type UserLicenseManagementPageProps = {
	onNavigate: NavigationCallback;
};

export const UserLicenseManagementPage = (props: UserLicenseManagementPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [statistics, setStatistics] = useState<readonly UserLicenseStatistics[]>([]);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.FeaturePackages);
	const [defaultUserLicenseDialog, openDefaultUserLicenseDialog] = useDefaultUserLicenseDialog();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const statistics: readonly UserLicenseStatistics[] = await getStatistics(signal)
						.catch(handlePageDataLoadingError);

					setStatistics(statistics);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const productName = useMemo(
		(): string => userConfiguration().branding
			.productName,
		[]
	);

	const defaultActions = useMemo(
		(): readonly UserLicenseManagementAction[] => {
			return listDefaultActions({
				manageDefaultPackage: openDefaultUserLicenseDialog
			});
		},
		[openDefaultUserLicenseDialog]
	);

	const topBarActions = useMemo(
		(): readonly UserLicenseManagementAction[] => {
			return buildActionsForTopBar<undefined, UserLicenseManagementAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const managePackageHandler = (kind: UserLicense): void => {
		onNavigate(
			userLicenseManagementRouting.edit({
				kind: `${kind}`
			})
		);
	};

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_Packages")}
		>
			<p
				{...setTrackingId("info-block")}
				className={styles.infoBlock}
			>
				<FormattedText
					text={localize("_PackagesManagementInformation")}
					thingsToReplace={[
						{
							what: "{{ProductName}}",
							filler: productName
						}
					]}
				/>
			</p>
			<UserLicenseList trackingId="package-list">
				{statistics.map(statistic => (
					<CardItem
						key={statistic.userLicense}
						license={statistic.userLicense}
						statistics={statistic}
						onManagePackage={managePackageHandler}
					/>
				))}
			</UserLicenseList>

			<AiAssistantFabSpacing />
			{defaultUserLicenseDialog}
		</SettingsPage>
	);
};
UserLicenseManagementPage.displayName = "UserLicenseManagementPage";

export const UserLicenseManagementPageForRouter = connectToRouter(
	UserLicenseManagementPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type CardItemProps = {
	license: UserLicense;
	statistics?: UserLicenseStatistics;
	onManagePackage: (kind: UserLicense) => void;
};

const CardItem = (props: CardItemProps): ReactElement => {
	const {
		license,
		statistics,
		onManagePackage
	} = props;

	const status = useMemo(
		(): UserLicenseCardStatus | undefined => {
			if (!statistics) {
				return undefined;
			}

			if (statistics.regularUserCount + statistics.guestUserCount > 0) {
				return "active";
			}

			return "inactive";
		},
		[statistics]
	);

	return (
		<UserLicenseCard
			trackingId={generateTrackingId("package", `|${license}`)}
		>
			<UserLicenseCard.Header
				kind={license}
				status={status}
			>
				{statistics && (
					<div
						{...setTrackingId("statistics")}
						className={styles.statistics}
					>
						<Icon
							value={{
								kind: "font",
								name: "user",
								color: "primary"
							}}
						/>
						<span>
							{statistics.regularUserCount + statistics.guestUserCount === 1
								? (
									localize("_OneUser")
								)
								: (
									<FormattedText
										text={localize("_AmountUsers")}
										thingsToReplace={[
											{
												what: "{{Number}}",
												filler: `${statistics.regularUserCount + statistics.guestUserCount}`
											}
										]}
									/>
								)
							}
						</span>
					</div>
				)}
			</UserLicenseCard.Header>
			<UserLicenseCard.Content>
				<UserLicenseDescription />
			</UserLicenseCard.Content>
			<UserLicenseCard.FooterButton
				kind={
					status === "inactive"
						? "primary"
						: "secondary"
				}
				text={localize("_ManageUsers")}
				onClick={() => onManagePackage(license)}
			/>
		</UserLicenseCard>
	);
};
CardItem.displayName = "CardItem";

type UserLicenseManagementAction = Action<undefined>;

type DefaultActionsHandlers = {
	manageDefaultPackage(): void;
};

function listDefaultActions(handlers: DefaultActionsHandlers): readonly UserLicenseManagementAction[] {
	const {
		manageDefaultPackage
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			icon: "gear",
			name: "ManageDefaultPackage",
			title: localize("_ManageDefaultPackage"),
			canExecute: () => true,
			execute: () => manageDefaultPackage()
		}
	];
}

export const userLicenseManagementRouting = {
	overview: "/FeaturePackageManagement",
	edit: route<EditUserLicensePageParams>`/FeaturePackageManagement/${"kind"}`
};